import { useState, useEffect, useMemo } from 'react';
import { Button, Pagination, Select, SelectItem, Input } from '@nextui-org/react';
import { TaskWithAsker } from 'common/src/utils/types';
import TaskTable from '../../components/tasks/taskTable/TaskTable';
import ModalCreateTask from '../../components/tasks/modalTask/modalCreateTask/ModalCreateTask';
import ModalDeleteTask from '../../components/tasks/modalTask/modalDeleteTask';
import useGeneralStore from '../../store/useGeneralStore';
import { FaPlus } from 'react-icons/fa';
import ModalTask from '../../components/tasks/modalTask/modalTask';
import { fetchTasksAdvanced } from 'common/src/api/tasks';
import useUserStore from '../../store/useUserStore';

// Add these state variables

export default function TasksManagementPage() {
  const [selectedUser, setSelectedUser] = useState<string>('');
  const { users = [] } = useGeneralStore();
  const [tasks, setTasks] = useState<TaskWithAsker[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskWithAsker | null>(null);
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [askerName, setAskerName] = useState('');
  const [askerLastName, setAskerLastName] = useState('');
  const [taskerName, setTaskerName] = useState('');
  const [taskerLastName, setTaskerLastName] = useState('');

  const { userProfile } = useUserStore();

  const loadTasks = async () => {
    setIsLoading(true);
    try {
      let assignedUserId: number | undefined;
      if (userProfile?.role.includes('admin') && selectedUser) {
        assignedUserId = parseInt(selectedUser);
      } else if (!userProfile?.role.includes('admin')) {
        assignedUserId = userProfile?.id;
      }

      const { data, totalPages: newTotalPages } = await fetchTasksAdvanced(
        page,
        10,
        sortOrder,
        undefined,
        assignedUserId,
        selectedStatus || undefined,
        askerName,
        askerLastName,
        taskerName,
        taskerLastName
      );
      setTasks(data || []);
      setTotalPages(newTotalPages);
      setError(null);
    } catch (err) {
      setError('Error al cargar las tareas');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    loadTasks();
  }, [selectedUser, selectedStatus, askerName, askerLastName, taskerName, taskerLastName]);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    sortOrder,
    selectedUser,
    selectedStatus,
    askerName,
    askerLastName,
    taskerName,
    taskerLastName,
  ]);

  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleEdit = (task: TaskWithAsker) => {
    setSelectedTask(task);
    setEditModalVisible(true);
  };

  const handleDelete = (task: TaskWithAsker) => {
    setSelectedTask(task);
    setDeleteModalVisible(true);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleOpenTaskModal = (task: TaskWithAsker) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [tasks, sortOrder]);

  return (
    <div className="container mx-auto px-4 h-full flex flex-col">
      <div className="items-center pb-4 space-y-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-2xl font-semibold">Gestión de Tareas</h2>
          <Button
            color="primary"
            size="sm"
            className="bg-purple-400 rounded-full"
            isIconOnly
            onPress={handleCreate}
          >
            <FaPlus size={18} />
          </Button>
        </div>
        {userProfile?.role.includes('admin') && (
          <div className="flex flex-wrap gap-4">
            <Select
              items={[
                { label: 'Todos los usuarios', value: '' },
                ...(users?.map((user) => ({
                  label: `${user.first_name} ${user.last_name}`,
                  value: user.id.toString(),
                })) || []),
              ]}
              label="Filtrar por usuario"
              placeholder="Seleccionar un usuario"
              className="max-w-xs"
              variant="bordered"
              onChange={(e) => setSelectedUser(e.target.value)}
              defaultSelectedKeys={['']}
            >
              {(user) => (
                <SelectItem key={user.value} value={user.value}>
                  {user.label}
                </SelectItem>
              )}
            </Select>
            <Select
              items={[
                { label: 'Todos los estados', value: '' },
                { label: 'Pendiente', value: 'pending' },
                { label: 'Aceptado', value: 'accepted' },
                { label: 'En aplicación', value: 'in_application' },
                { label: 'Asignado', value: 'assigned' },
                { label: 'En progreso', value: 'in_progress' },
                { label: 'Completado', value: 'complete' },
                { label: 'Rechazado', value: 'rejected' },
              ]}
              label="Filtrar por estado"
              placeholder="Seleccionar un estado"
              className="max-w-xs"
              variant="bordered"
              onChange={(e) => setSelectedStatus(e.target.value)}
              defaultSelectedKeys={['']}
            >
              {(status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              )}
            </Select>
            <Input
              label="Nombre del Asker"
              placeholder="Ingrese nombre"
              value={askerName}
              onChange={(e) => setAskerName(e.target.value)}
              className="max-w-xs"
            />
            <Input
              label="Apellido del Asker"
              placeholder="Ingrese apellido"
              value={askerLastName}
              onChange={(e) => setAskerLastName(e.target.value)}
              className="max-w-xs"
            />
            <Input
              label="Nombre del Tasker"
              placeholder="Ingrese nombre"
              value={taskerName}
              onChange={(e) => setTaskerName(e.target.value)}
              className="max-w-xs"
            />
            <Input
              label="Apellido del Tasker"
              placeholder="Ingrese apellido"
              value={taskerLastName}
              onChange={(e) => setTaskerLastName(e.target.value)}
              className="max-w-xs"
            />
          </div>
        )}
      </div>

      <div className="flex-grow">
        {isLoading ? (
          <p>Cargando tareas...</p>
        ) : error ? (
          <p>Error al cargar tareas: {error}</p>
        ) : (
          <div className="h-full">
            <TaskTable
              tasks={sortedTasks}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onOpenTaskModal={handleOpenTaskModal}
            />
            <div className="flex justify-center mt-4">
              <Pagination
                total={totalPages}
                initialPage={1}
                page={page}
                onChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </div>

      <ModalCreateTask
        isOpen={createModalVisible}
        onClose={() => {
          setCreateModalVisible(false);
        }}
        fetchTasks={loadTasks}
        isEditMode={false}
      />

      {editModalVisible && selectedTask && (
        <ModalCreateTask
          isOpen={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
          }}
          task={selectedTask}
          fetchTasks={loadTasks}
          isEditMode={true}
        />
      )}

      {deleteModalVisible && selectedTask && (
        <ModalDeleteTask
          isOpen={deleteModalVisible}
          onClose={() => {
            setDeleteModalVisible(false);
          }}
          task={selectedTask}
          fetchTasks={loadTasks}
        />
      )}

      {isTaskModalOpen && selectedTask && (
        <ModalTask
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          task={selectedTask}
          onReset={() => setSelectedTask(null)}
          fetchTasks={loadTasks}
        />
      )}
    </div>
  );
}
